import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { ToastrService } from '@app/services/toastr.service'
import { UploadTypeEnum } from '@app/v2/auth/types/media.type'
import { PublicInfoIcons } from '@app/v2/settings/models/organization-settings.model'
import { ProfileService } from '@app/v2/settings/services/profile.service'
import { Authenticator, MFA_FACTOR_TYPE } from '@app/v2/settings/types/mfa.types'
import { CreateIdentifierResponse } from '@app/v2/settings/types/user-identifier'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { EmailInputComponent } from '@app/v2/shared/components/inputs/email-input/email-input.component'
import { PhoneInputComponent } from '@app/v2/shared/components/inputs/phone-input/phone-input.component'
import { LoadingScreenComponent } from '@app/v2/shared/components/loading-screen/loading-screen.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { UploadComponent } from '@app/v2/shared/components/upload/upload.component'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { serializeAttestationCredential, toCreationOptionsFromServer } from '@app/v2/shared/helpers/webauthn'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { ValidateFullName } from '@app/validators/validate-full-name'
import { ValidateJobTitle } from '@app/validators/validate-job-title'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { PhoneValidator } from '@micetribe/forms/validators/phone.validator'
import { RequiredPhoneValidator } from '@micetribe/forms/validators/required-phone.validator'
import { debounceTime, firstValueFrom, Subject } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'

/**
 * User profile component
 */
@Component({
  selector: 'ngx-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    FormsModule,
    BasicInputComponent,
    PhoneInputComponent,
    EmailInputComponent,
    UploadComponent,
    MatDialogModule,
    LoadingScreenComponent,
    ModalComponent
  ]
})
export class ProfileComponent implements OnInit, AfterViewInit {
  /** Debounced autosave for "My info" (match org settings save-on-blur) */
  private _myInfoBlurSubject = new Subject<void>()
  private _myInfoUserHasFinishedEditing = false
  /** Flag to track if alternative emails are loaded */
  public emailsLoaded = false
  /** Flag to track if user was redirected due to weak password */
  public showPasswordWarning = false
  /** Expose enum for template */
  public readonly MFA_FACTOR_TYPE = MFA_FACTOR_TYPE
  /** Expose global encoder for template URLs */
  public readonly encodeURIComponent = encodeURIComponent
  /** Expose shared enums for template (match v2 inputs usage) */
  public readonly BasicInputMode = BasicInputMode
  public readonly basicFieldType = BasicFieldType
  public readonly uploadTypeEnum = UploadTypeEnum
  public readonly profileIcons = {
    user: `<svg width="16" height="16" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 14 18">
      <path d='M7 9a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm2 1H5a5.006 5.006 0 0 0-5 5v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2a5.006 5.006 0 0 0-5-5Z'/>
    </svg>`,
    job: `<svg width="16" height="16" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
      <path d='M7 2a1 1 0 0 0-1 1v1H4a2 2 0 0 0-2 2v2h16V6a2 2 0 0 0-2-2h-2V3a1 1 0 0 0-1-1H7Zm1 2V4h4v2H8Zm10 6H2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6Z'/>
    </svg>`,
    email: PublicInfoIcons.getIconsConfiguration().email,
    phone: PublicInfoIcons.getIconsConfiguration().phone
  }

  /** Sidebar icons (match organization settings: inline SVG + active color) */
  public readonly profileSidebarIcons = {
    account: `<svg
      width="14"
      height="14"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20">
      <path
        d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z" />
    </svg>`,
    email: `<svg
      width="14"
      height="14"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20">
      <path
        d="M2 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v.3l-8 5-8-5V6Zm0 2.7V14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.7l-7.47 4.67a1 1 0 0 1-1.06 0L2 8.7Z"
      />
    </svg>`,
    password: `<svg
      width="14"
      height="14"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20">
      <path
        d="M5 8V6a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h1Zm2 0h6V6a3 3 0 0 0-6 0v2Z"
      />
    </svg>`,
    mfa: `<svg
      width="14"
      height="14"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20">
      <path
        d="M10 1.5 3 4.5V10c0 5 3 7.5 7 9.5 4-2 7-4.5 7-9.5V4.5l-7-3Zm0 6a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm0 8a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z"
      />
    </svg>`,
    passkeys: `<svg
      width="14"
      height="14"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20">
      <path
        d="M10 0a5 5 0 0 1 5 5v1h1a2 2 0 0 1 2 2v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a2 2 0 0 1 2-2h1V5a5 5 0 0 1 5-5Zm3 6V5a3 3 0 0 0-6 0v1h6Zm-3 4a1 1 0 0 0-1 1v3a1 1 0 1 0 2 0v-3a1 1 0 0 0-1-1Z"
      />
    </svg>`,
    devices: `<svg
      width="14"
      height="14"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20">
      <path
        d="M3 4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4Zm2 0v8h10V4H5Zm-1 12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1H4v1Z"
      />
    </svg>`
  }

  public getProfileSidebarIcon(icon: string, isActive: boolean): SafeHtml {
    const iconClass = isActive ? 'tw-text-white' : 'tw-text-gray-500 dark:tw-text-gray-400'
    const iconWithClass = icon.replace('<svg', `<svg class="${iconClass}"`)
    return this._sanitizer.bypassSecurityTrustHtml(iconWithClass)
  }

  /** v2 upload component file list for profile picture */
  public profilePictureFile = new FormGroup({
    path: new FormControl(''),
    id: new FormControl(null),
    name: new FormControl(null)
  })
  /**
   * Legacy: kept for compatibility with `CloudinaryUploadService` typing.
   * Profile picture uploads are now handled by the v2 `app-upload` component.
   */
  public cloudinaryUploadWidget: any
  /** Which MFA factor is currently enabled; only one at a time. */
  public enabledMFAType: MFA_FACTOR_TYPE | null = null
  /** Currently active sidebar tab (for scroll spy and highlight) */
  public activeTab: 'account' | 'email' | 'mfa' | 'passkeys' | 'devices' | 'password' = 'account'
  /** Modal id for the MFA setup/disable modal */
  public readonly mfaModalId = 'profile-mfa-modal'
  /** When true, the MFA modal is rendered and shown (same pattern as org-modal @if (isOpen())). */
  public mfaModalOpen = false
  /** Which MFA factor the modal is for */
  public mfaModalFactor: MFA_FACTOR_TYPE | null = null
  /** Whether the modal is for enabling or disabling MFA */
  public mfaModalMode: 'enable' | 'disable' | null = null
  /** Loading state while fetching setup content or submitting */
  public mfaModalLoading = false
  /** Authenticator app: response from POST auth/mfa/enable (QR + recovery codes) */
  public authenticatorResponse: Authenticator | null = null
  /** Optional recovery code (backup) for disable flow */
  public mfaRecoveryCodeControl = new FormControl('')
  /** OTP controls for MFA confirm (6 boxes) */
  public readonly mfaOtpControls: FormControl[] = Array.from({ length: 6 }, () => new FormControl(''))
  /** Authenticator app only: true after confirm success – show recovery codes step */
  public mfaShowRecoveryCodes = false
  /** Email/SMS only: seconds remaining before the user can resend the code */
  public mfaResendSecondsRemaining = 0

  /** Password change verification (step-up) */
  public readonly passwordVerifyModalId = 'profile-password-verify-modal'
  public passwordVerifyModalOpen = false
  public passwordVerifyLoading = false
  public passwordVerifyMode: 'mfa' | 'email' = 'mfa'
  /** OTP controls for password verify (6 boxes) */
  public readonly passwordVerifyOtpControls: FormControl[] = Array.from({ length: 6 }, () => new FormControl(''))

  /** Current password modal (used when MFA is disabled) */
  public readonly currentPasswordModalId = 'profile-current-password-modal'
  public currentPasswordModalOpen = false
  public currentPasswordLoading = false
  public currentPasswordControl = new FormControl('', { nonNullable: true, validators: [Validators.required] })

  /** Timer id for the resend cooldown */
  private _mfaResendTimer: ReturnType<typeof setInterval> | null = null
  private readonly _destroyRef = inject(DestroyRef)

  ngOnDestroy(): void {
    if (this._mfaResendTimer) {
      clearInterval(this._mfaResendTimer)
      this._mfaResendTimer = null
    }
    this._myInfoBlurSubject.complete()
  }

  /**
   * User profile form group
   */
  profileForm: UntypedFormGroup
  /**
   * User password form group
   */
  passwordForm: UntypedFormGroup
  /**
   * Whether user have an avatar
   */
  hasAvatar = false
  /**
   * Maximum number of emails allowed
   */
  readonly _MAX_EMAILS = 2

  /**
   * Email validation pattern - allows all special characters before @ symbol, strict domain validation
   */
  private readonly EMAIL_VALIDATION_PATTERN = /^[^@\s]+@[a-zA-Z0-9\-_.]+$/

  /**
   * When true, temporary flag to ignore scroll spy updates during
   * programmatic scrolls triggered by the tab buttons.
   */
  private _suppressScrollSpy = false
  /**
   * Sets the active sidebar tab (updates highlight and can be used before scrolling).
   */
  public setActiveTab(tab: 'account' | 'email' | 'mfa' | 'passkeys' | 'devices' | 'password'): void {
    this.activeTab = tab
  }

  /** Top offset (px) when scrolling so the section starts below the fixed header. */
  private readonly _sectionScrollOffset = 110

  /**
   * Creates the component and injects it's dependencies
   * @param fb FormBuilder
   * @param auth AuthService
   * @param toastr ToastrService
   * @param dialog MatDialog
   */
  constructor(
    private fb: UntypedFormBuilder,
    private _sanitizer: DomSanitizer,
    public auth: AuthService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private profileService: ProfileService,
    private route: ActivatedRoute,
    private _modalService: ModalService
  ) {
    // Debounce blur events to prevent duplicate updates (same UX as org settings)
    this._myInfoBlurSubject.pipe(debounceTime(300), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (!this._myInfoUserHasFinishedEditing) return
      this._myInfoUserHasFinishedEditing = false
      if (this.isMainFormReady) this.save()
    })
  }

  /** "My info" autosave handlers */
  public onMyInfoBlur(): void {
    this._myInfoUserHasFinishedEditing = true
    this._myInfoBlurSubject.next()
  }

  public onMyInfoKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return
    event.preventDefault()
    event.stopPropagation()
    this._myInfoUserHasFinishedEditing = true
    if (this.isMainFormReady) this.save()
  }

  /**
   * Get emails form array
   */
  get emailsArray(): UntypedFormArray {
    return this.profileForm.get('emails') as UntypedFormArray
  }

  /**
   * Check if can add more emails
   */
  get canAddEmail(): boolean {
    return this.emailsArray.length < this._MAX_EMAILS
  }

  /**
   * Check if the main form is valid and not pristine (ignoring alternative emails)
   * Phone number is required - button only enabled if phone has value and at least one field is modified
   */
  get isMainFormReady(): boolean {
    const mainControls = ['profile_picture', 'full_name', 'job_title', 'primary_email', 'phone_number']
    let isPristine = true
    // If phone number is empty, button should be disabled
    if (!this.profileForm.get('phone_number')?.value || !this.profileForm.get('phone_number')?.value.trim()) return false

    for (const controlName of mainControls) {
      const control = this.profileForm.get(controlName)
      if (control) {
        if (control.invalid && control.dirty) return false
        if (control.dirty) isPristine = false
      }
    }

    return !isPristine
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.profileForm = this.fb.group({
      profile_picture: [this.auth.user?.profile_picture ?? ''],
      full_name: [this.auth.user?.full_name ?? '', [ValidateFullName]],
      job_title: [this.auth.user?.job_title ?? '', [ValidateJobTitle]],
      primary_email: [this.auth.user?.email ?? '', [Validators.email]],
      emails: this.fb.array([], [this._uniqueEmailsValidator()]),
      phone_number: [this.auth.user?.phone_number, [RequiredPhoneValidator.validateWith({ defaultCountry: '' }), PhoneValidator.validateWith({ defaultCountry: '' })]]
    })

    // Primary email is display-only on profile settings
    this.profileForm.controls['primary_email']?.disable({ emitEvent: false })

    // Initialize v2 upload file list from existing profile picture
    this.profilePictureFile.patchValue({
      path: this.profileForm.controls['profile_picture']?.value ?? ''
    })

    this._initializeEmails()

    this.passwordForm = this.fb.group(
      {
        new_password: [null, [Validators.required, Validators.pattern(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&^_-]).{8,}/)]],
        new_password_confirmation: [null, [Validators.required]]
      },
      {
        validators: this._confirmedValidator('new_password', 'new_password_confirmation')
      }
    )
    this.hasAvatar = !!this.auth.user?.profile_picture
    this.upperCase(['full_name', 'job_title'])
    this._getIdentifiers()
    this._loadPasskeys()
    this._loadSessions()

    // Initialize MFA UI state from auth user (and keep it in sync on refresh).
    this._syncMfaUiStateFromAuthUser()
    this.auth.userChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._syncMfaUiStateFromAuthUser())
  }

  private _syncMfaUiStateFromAuthUser(): void {
    const mfaEnabled = !!(this.auth.user as any)?.mfa_enabled
    const channel = String((this.auth.user as any)?.mfa_channel ?? '').toUpperCase()
    if (!mfaEnabled) {
      this.enabledMFAType = null
      return
    }
    if (channel === 'APP') {
      this.enabledMFAType = MFA_FACTOR_TYPE.APP
      return
    }
    if (channel === 'EMAIL') {
      this.enabledMFAType = MFA_FACTOR_TYPE.EMAIL
      return
    }
    if (channel === 'SMS') {
      this.enabledMFAType = MFA_FACTOR_TYPE.PHONE
      return
    }
    this.enabledMFAType = null
  }

  /**
   * Handles fragment scrolling after view is initialized
   */
  ngAfterViewInit(): void {
    // Check if there's a fragment in the URL and scroll to the security/change-password section
    this.route.fragment.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((fragment) => {
      if (fragment === 'change-password' || fragment === 'password') {
        this.showPasswordWarning = true
        this.activeTab = 'password'
        // Wait for emails to load, then scroll
        const checkEmailsLoaded = setInterval(() => {
          if (this.emailsLoaded) {
            clearInterval(checkEmailsLoaded)
            const element = document.getElementById('password')
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }
        })
      }
    })
  }

  /**
   * Scrolls the page so the section start is at the top (My Info) or 80px from top (Security, below header).
   */
  public scrollToSection(sectionId: string): void {
    if (sectionId === 'account') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    const element = document.getElementById(sectionId)
    if (element) {
      this._suppressScrollSpy = true
      const sectionTop = element.getBoundingClientRect().top + window.scrollY
      const offset = this._sectionScrollOffset
      const top = Math.max(0, sectionTop - offset)
      window.scrollTo({ top, behavior: 'smooth' })
      setTimeout(() => {
        this._suppressScrollSpy = false
      }, 400)
    }
  }

  /**
   * Updates the active tab based on scroll position (scroll spy) so the sidebar reflects the section in view.
   */
  @HostListener('window:scroll', [])
  private _onWindowScroll(): void {
    if (!this.emailsLoaded || this._suppressScrollSpy) return
    const accountEl = document.getElementById('account')
    const emailEl = document.getElementById('email-management')
    const devicesEl = document.getElementById('linked-devices')
    const passwordEl = document.getElementById('password')
    const mfaEl = document.getElementById('mfa')
    const passkeysEl = document.getElementById('passkeys')
    if (!accountEl || !emailEl || !mfaEl || !passkeysEl || !devicesEl || !passwordEl) return
    const viewportMiddle = window.innerHeight / 2
    const sections: Array<{ id: string; tab: 'account' | 'email' | 'mfa' | 'passkeys' | 'devices' | 'password' }> = [
      { id: 'account', tab: 'account' },
      { id: 'email-management', tab: 'email' },
      { id: 'mfa', tab: 'mfa' },
      { id: 'passkeys', tab: 'passkeys' },
      { id: 'linked-devices', tab: 'devices' },
      { id: 'password', tab: 'password' }
    ]

    const distances = sections
      .map(({ id, tab }) => {
        const el = document.getElementById(id)
        if (!el) return null
        const rect = el.getBoundingClientRect()
        const center = rect.top + rect.height / 2
        return { tab, distance: Math.abs(center - viewportMiddle) }
      })
      .filter(Boolean) as Array<{ tab: 'account' | 'email' | 'mfa' | 'passkeys' | 'devices' | 'password'; distance: number }>

    if (!distances.length) return
    distances.sort((a, b) => a.distance - b.distance)
    const newTab = distances[0].tab
    if (newTab !== this.activeTab) this.activeTab = newTab
  }

  // ---- Linked devices (sessions) ----

  // ---- Passkeys (WebAuthn) ----
  public passkeysLoaded = false
  public passkeysLoading = false
  public passkeys: Array<{ id: number; name: string | null; created_at: string | null; last_used_at: string | null }> = []
  public passkeyNameControl = new FormControl<string>('', { nonNullable: true })
  public addingPasskey = false
  public deletingPasskeyId: number | null = null

  public devicesLoaded = false
  public sessions: Array<{
    id: string
    ip_address: string | null
    user_agent: string | null
    last_activity: number
    last_activity_human: string
    is_current?: boolean
    location?: {
      city?: string | null
      region?: string | null
      country?: string | null
      map_query: string
      lat?: number | null
      lon?: number | null
    } | null
  }> = []
  public revokingSessionId: string | null = null
  public revokingAllSessions = false

  /** Location modal (static map) */
  public readonly deviceLocationModalId = 'profile-device-location-modal'
  public deviceLocationModalOpen = false
  public deviceLocationLoading = false
  public deviceLocationLabel = ''
  public deviceLocationLatLng: { lat: number; lon: number } | null = null
  public deviceLocationMapUrl: string | null = null
  public deviceLocationGoogleMapsUrl: string | null = null

  private _loadPasskeys(): void {
    this.passkeysLoading = true
    this.profileService
      .listPasskeys()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          const data = (res as any)?.data ?? res
          this.passkeys = data?.passkeys ?? []
          this.passkeysLoaded = true
          this.passkeysLoading = false
        },
        error: () => {
          this.passkeysLoaded = true
          this.passkeysLoading = false
          this.toastr.error('Failed to load passkeys', '')
        }
      })
  }

  public async addPasskey(): Promise<void> {
    if (this.addingPasskey) return
    this.addingPasskey = true
    try {
      if (!('PublicKeyCredential' in window) || typeof navigator.credentials?.create !== 'function') {
        this.toastr.error('Passkeys are not supported on this browser', '')
        return
      }

      const optionsRes = await firstValueFrom(this.profileService.getPasskeyRegistrationOptions())
      const data = (optionsRes as any)?.data ?? optionsRes
      const requestId = data?.request_id
      const publicKeyFromServer = data?.publicKey
      if (!requestId || !publicKeyFromServer) {
        this.toastr.error('Could not start passkey registration', '')
        return
      }

      const publicKey = toCreationOptionsFromServer(publicKeyFromServer)
      const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
      if (!cred) return

      const credentialPayload = serializeAttestationCredential(cred)
      await firstValueFrom(
        this.profileService.verifyPasskeyRegistration({
          request_id: requestId,
          credential: credentialPayload,
          name: this.passkeyNameControl.value?.trim() || null
        })
      )
      this.passkeyNameControl.setValue('')
      this.toastr.success('Passkey added', '')
      this._loadPasskeys()
    } catch (e: any) {
      this.toastr.error('Failed to add passkey', 'No PassKey was created')
    } finally {
      this.addingPasskey = false
    }
  }

  public deletePasskey(passkeyId: number): void {
    if (this.deletingPasskeyId) return
    this.deletingPasskeyId = passkeyId
    this.profileService
      .deletePasskey(passkeyId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.passkeys = this.passkeys.filter((p) => p.id !== passkeyId)
          this.toastr.success('Passkey removed', '')
          this.deletingPasskeyId = null
        },
        error: (error) => {
          this.toastr.error('Failed to remove passkey', `${error?.error?.message ?? ''}`)
          this.deletingPasskeyId = null
        }
      })
  }

  private _loadSessions(): void {
    this.profileService
      .getSessions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          const data = (res as any)?.data ?? res
          this.sessions = data?.sessions ?? []
          this.devicesLoaded = true
        },
        error: () => {
          this.devicesLoaded = true
          this.toastr.error('Failed to load linked devices', '')
        }
      })
  }

  public getDeviceTitle(session: { user_agent: string | null }): string {
    const ua = session.user_agent ?? ''
    const browser = ua.includes('Arc/')
      ? 'Arc'
      : ua.includes('Edg/')
      ? 'Edge'
      : ua.includes('Chrome/')
      ? 'Chrome'
      : ua.includes('Safari/') && !ua.includes('Chrome/')
      ? 'Safari'
      : ua.includes('Firefox/')
      ? 'Firefox'
      : 'Browser'
    const os = ua.includes('Mac OS X')
      ? 'macOS'
      : ua.includes('Windows NT')
      ? 'Windows'
      : ua.includes('Android')
      ? 'Android'
      : ua.includes('iPhone') || ua.includes('iPad')
      ? 'iOS'
      : 'OS'
    return `${browser} on ${os}`
  }

  public isCurrentSession(session: { id: string; is_current?: boolean }): boolean {
    return session.is_current === true
  }

  public revokeSession(sessionId: string): void {
    this.revokingSessionId = sessionId
    this.profileService
      .deleteSession(sessionId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.sessions = this.sessions.filter((s) => s.id !== sessionId)
          this.toastr.success('Logged out from device', '')
          this.revokingSessionId = null
        },
        error: (error) => {
          this.toastr.error('Failed to log out from device', `${error?.error?.message ?? ''}`)
          this.revokingSessionId = null
        }
      })
  }

  public revokeOtherSessions(): void {
    this.revokingAllSessions = true
    this.profileService
      .deleteOtherSessions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.sessions = this.sessions.filter((s) => s.is_current === true)
          this.toastr.success('Logged out from other devices', '')
          this.revokingAllSessions = false
        },
        error: (error) => {
          this.toastr.error('Failed to log out from devices', `${error?.error?.message ?? ''}`)
          this.revokingAllSessions = false
        }
      })
  }

  public hasSessionLocation(session: { location?: { map_query?: string } | null }): boolean {
    return !!String(session.location?.map_query ?? '').trim()
  }

  /**
   * Opens Google Maps (no API key).
   * Prefer ipwho.is coordinates when present (same source as manual IP lookups); else place search.
   */
  public openDeviceLocation(session: { location?: { map_query?: string; lat?: number | null; lon?: number | null } | null }): void {
    const loc = session.location
    const lat = loc?.lat
    const lon = loc?.lon
    if (typeof lat === 'number' && typeof lon === 'number' && Number.isFinite(lat) && Number.isFinite(lon)) {
      window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank', 'noopener,noreferrer')
      return
    }
    const query = String(loc?.map_query ?? '').trim()
    if (!query) return
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer')
  }

  public getSessionLocationLabel(session: { location?: { city?: string | null; region?: string | null; country?: string | null; map_query?: string } | null }): string {
    const loc = session.location ?? null
    const fromQuery = String(loc?.map_query ?? '').trim()
    if (fromQuery) return fromQuery
    const label = [loc?.city, loc?.region, loc?.country]
      .map((p) => String(p ?? '').trim())
      .filter(Boolean)
      .join(', ')
    return label || 'Unknown location'
  }

  private _applyDeviceLocation(loc: { lat: number; lon: number; label: string }): void {
    this.deviceLocationLabel = loc.label
    this.deviceLocationLatLng = { lat: loc.lat, lon: loc.lon }
    const key = String((environment as any)?.googleMapsAPIKey ?? '').trim()
    if (key) {
      const center = `${loc.lat},${loc.lon}`
      const markers = `color:red|${center}`
      this.deviceLocationMapUrl =
        `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(center)}` +
        `&zoom=11&size=700x360&scale=2&maptype=roadmap&markers=${encodeURIComponent(markers)}` +
        `&key=${encodeURIComponent(key)}`
    } else {
      this.deviceLocationMapUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${loc.lat},${loc.lon}&zoom=11&size=700x360&markers=${loc.lat},${loc.lon},red-pushpin`
    }
    this.deviceLocationGoogleMapsUrl = `https://www.google.com/maps?q=${loc.lat},${loc.lon}`
  }

  public closeDeviceLocation(): void {
    this.deviceLocationModalOpen = false
    this.deviceLocationLoading = false
  }

  /**
   * Custom validator to ensure emails are unique
   */
  private _uniqueEmailsValidator() {
    return (formArray: UntypedFormArray) => {
      const primaryEmailControl = this.profileForm?.get('primary_email')
      if (!primaryEmailControl) return null

      const primaryEmail = primaryEmailControl.value?.toLowerCase().trim()
      const alternativeEmails = formArray.controls.map((control) => control.get('email').value?.toLowerCase().trim()).filter((email) => email && email.length > 0) // Only check non-empty emails

      // Check if any alternative email is the same as primary email
      const hasPrimaryDuplicate = alternativeEmails.some((email) => email === primaryEmail)

      // Check if there are duplicate alternative emails
      const uniqueAlternativeEmails = new Set(alternativeEmails)
      const hasAlternativeDuplicates = alternativeEmails.length !== uniqueAlternativeEmails.size

      if (hasPrimaryDuplicate || hasAlternativeDuplicates) return { duplicateEmails: true }

      return null
    }
  }

  /**
   * Initialize emails array with primary email
   */
  private _initializeEmails() {
    // If we have user data with identifiers, load alternative emails
    if (this.auth.user && (this.auth.user as any).identifiers && Array.isArray((this.auth.user as any).identifiers)) {
      this._loadEmailsFromIdentifiers((this.auth.user as any).identifiers)
    }
  }

  /**
   * Load emails from identifiers array
   */
  private _loadEmailsFromIdentifiers(identifiers: any[]) {
    const emailIdentifiers = identifiers.filter((identifier) => identifier.type === 'EMAIL')

    // Filter out the primary email (first one) and only add alternative emails
    const alternativeEmails = emailIdentifiers.slice(1)

    // Only add alternative emails if they exist - don't add default
    alternativeEmails.forEach((emailIdentifier) => {
      this.addEmail(emailIdentifier.identifier, emailIdentifier.is_verified === 1, emailIdentifier.id)
    })
  }

  /**
   * Check if email has been modified from its original value
   */
  public isEmailModified(index: number): boolean {
    const emailGroup = this.emailsArray.at(index)
    return emailGroup.get('is_modified').value
  }

  /**
   * Check if email is valid and can be verified
   */
  public isEmailValid(index: number): boolean {
    const emailControl = this.emailsArray.at(index).get('email')
    const emailValue = emailControl.value

    // Check if email has a value and is not empty
    if (!emailValue || !emailValue.trim()) {
      return false
    }

    // Check if email has proper format (contains @ and domain) - allowing all special characters before @
    return this.EMAIL_VALIDATION_PATTERN.test(emailValue.trim())
  }

  /**
   * Check if email is already verified
   */
  public isEmailVerified(index: number): boolean {
    return this.emailsArray.at(index).get('is_verified').value
  }

  /**
   * Check if email is currently being created
   */
  public isEmailCreating(index: number): boolean {
    return this.emailsArray.at(index).get('is_creating').value
  }

  /**
   * Create identifier for the email at the specified index
   */
  public createIdentifier(index: number) {
    const emailGroup = this.emailsArray.at(index)
    const email = emailGroup.get('email').value
    const identifierId = emailGroup.get('id').value

    if (!email || !this.isEmailValid(index)) {
      this.toastr.error('Please enter a valid email address', '')
      return
    }

    // Check for duplicates before calling API
    const emailLower = email.toLowerCase().trim()
    const primaryEmail = this.profileForm.get('primary_email').value?.toLowerCase().trim()

    // Check if email is same as primary email
    if (emailLower === primaryEmail) {
      this.toastr.error('Email cannot be the same as primary email', '')
      return
    }

    // Check for duplicates with other alternative emails
    const alternativeEmails = this.emailsArray.controls
      .map((control) => control.get('email').value?.toLowerCase().trim())
      .filter((altEmail, altIndex) => altEmail && altIndex !== index)

    if (alternativeEmails.includes(emailLower)) {
      this.toastr.error('Duplicate email addresses are not allowed', '')
      return
    }

    // For update scenario, check if email actually changed
    if (identifierId && !this.isEmailModified(index)) {
      this.toastr.info('Email value has not changed', '')
      return
    }

    // Set creating state
    emailGroup.get('is_creating').setValue(true)

    const identifierData = {
      type: 'EMAIL' as const,
      identifier: email.trim(),
      is_primary: false
    }

    // If identifier already exists, update it; otherwise create new one
    if (identifierId) {
      // Update existing identifier
      this.profileService
        .updateIdentifier(identifierId, identifierData)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (response) => {
            emailGroup.patchValue({
              is_verified: true,
              is_creating: false,
              original_email: email.trim(), // Update original email to new value
              is_modified: false // Reset modification state
            })
            this.toastr.success('Email updated successfully', '')
            this._removeFocusFromEmailInput(index)
          },
          error: () => {
            emailGroup.get('is_creating').setValue(false)
          }
        })
    } else {
      // Create new identifier
      this.profileService
        .createIdentifier(identifierData)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (response: Response<CreateIdentifierResponse>) => {
            // Update the email group with the response data
            emailGroup.patchValue({
              id: response.data.user_identifier.id,
              is_verified: true,
              is_creating: false,
              original_email: email.trim(), // Set original email to current value
              is_modified: false // Reset modification state
            })
            this.toastr.success('Email added successfully', '')
            this._removeFocusFromEmailInput(index)
          },
          error: () => {
            emailGroup.get('is_creating').setValue(false)
          }
        })
    }
  }

  /**
   * Remove focus from email input field at specified index
   * @param index
   */
  private _removeFocusFromEmailInput(index: number) {
    // Find the email input element by index
    const emailInputs = document.querySelectorAll('.email-field-container input[type="text"]')
    if (emailInputs[index]) (emailInputs[index] as HTMLInputElement).blur()
  }

  /**
   * Get all user identifiers
   */
  private _getIdentifiers() {
    this.profileService
      .getIdentifiers()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: any) => {
          // Clear existing alternative emails
          while (this.emailsArray.length !== 0) {
            this.emailsArray.removeAt(0)
          }

          if (response.data && Array.isArray(response.data)) {
            const emailIdentifiers = response.data.filter((identifier: any) => identifier.type === 'EMAIL' && identifier.is_primary === 0)

            emailIdentifiers.forEach((emailIdentifier: any) => {
              this.addEmail(
                emailIdentifier.identifier,
                true, // Mark as verified since it's already in the system
                emailIdentifier.id
              )
            })
          }
          this.emailsLoaded = true
        },
        error: (error) => {
          // If session is gone, global interceptors will restart SSO flow; don't spam toasts.
          if (error?.status !== 401 && error?.status !== 403) {
            this.toastr.error('Failed to get identifiers', `${error?.error?.message ?? ''}`)
          }
          this.emailsLoaded = true
        }
      })
  }

  /**
   * Add a new email field (only for alternative emails)
   */
  public addEmail(email: string = '', isVerified: boolean = false, id: number = null) {
    const emailGroup = this.fb.group({
      email: [email, [Validators.email, Validators.pattern(this.EMAIL_VALIDATION_PATTERN)]],
      is_primary: [false],
      is_verified: [isVerified],
      id: [id],
      is_valid: [false], // Track if email is valid for showing verify button
      is_creating: [false], // Track if we're currently creating the identifier
      original_email: [email], // Track the original email value for comparison
      is_modified: [false] // Track if email has been modified from original
    })

    this.emailsArray.push(emailGroup)

    // Focus on the newly added email input field only if it's empty (new field)
    if (!email) {
      setTimeout(() => {
        const emailInputs = document.querySelectorAll('.email-field-container input[type="text"]')
        const newEmailIndex = this.emailsArray.length - 1
        if (emailInputs[newEmailIndex]) {
          ;(emailInputs[newEmailIndex] as HTMLInputElement).focus()
        }
      }, 0)
    }

    // Set up value change listener for the new email field
    const emailControl = emailGroup.get('email')
    emailControl.valueChanges
      .pipe(
        distinctUntilChanged(), // Only trigger if value actually changed
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((value) => {
        const index = this.emailsArray.controls.indexOf(emailGroup)
        if (index !== -1) {
          // Update is_valid based on strict email validation
          const emailValue = value
          let isValid = false

          if (emailValue && emailValue.trim()) {
            isValid = this.EMAIL_VALIDATION_PATTERN.test(emailValue.trim())
          }

          emailGroup.get('is_valid').setValue(isValid)

          // Check if email has been modified from original
          const originalEmail = emailGroup.get('original_email').value?.toLowerCase().trim()
          const currentEmail = emailValue?.toLowerCase().trim()
          const isModified = currentEmail !== originalEmail

          emailGroup.get('is_modified').setValue(isModified)
        }
      })
  }

  /**
   * Remove email at specific index
   * @param index
   */
  public deleteIdentifier(index: number) {
    const emailGroup = this.emailsArray.at(index)
    const identifierId = emailGroup.get('id').value
    const email = emailGroup.get('email').value

    // If email is empty and no ID (new empty field), just remove it without dialog
    if (!email || email.trim() === '') {
      this.emailsArray.removeAt(index)
      return
    }

    // If email has no ID (was never saved to database), just remove from UI without dialog
    if (!identifierId) {
      this.emailsArray.removeAt(index)
      return
    }

    // For emails that exist in database, show confirmation dialog and call delete API
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        headerText: 'Confirm Deletion',
        messageText: `Are you sure you want to delete the email address <strong>${email}</strong>? This action cannot be undone.`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'delete',
        allowHtml: true // Custom property to indicate HTML content
      },
      width: '400px',
      panelClass: 'matdialog'
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          // Call delete API for emails that exist in database
          this.profileService
            .deleteIdentifier(identifierId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: () => {
                // Remove from form array after successful API call
                this.emailsArray.removeAt(index)
                this.toastr.success('Email deleted successfully', '')
              }
            })
        }
      })
  }

  /**
   * Handle enter key press on email field - auto-create identifier if email is valid
   */
  public onEmailEnter(index: number, event: KeyboardEvent) {
    // Only trigger on Enter key
    if (event.key === 'Enter') {
      if (this.isEmailValid(index) && !this.isEmailVerified(index) && !this.isEmailCreating(index)) {
        this.createIdentifier(index)
      }
    }
  }

  /**
   * Global Enter key listener to handle email verification
   */
  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(event: KeyboardEvent) {
    // Check if the focused element is an input
    const target = event.target as HTMLInputElement
    if (target && target.tagName === 'INPUT') {
      // Find which email field is focused by checking if it's in our emails array
      const emailIndex = this._findEmailIndexByInput(target)
      if (emailIndex !== -1) {
        // Allow API call if email is valid and not currently being created
        // Remove the !isEmailVerified check to allow updates to existing emails
        if (this.isEmailValid(emailIndex) && !this.isEmailCreating(emailIndex)) {
          this.createIdentifier(emailIndex)
        }
      }
    }
  }

  /**
   * Find the index of an email in the emails array based on the input element
   */
  private _findEmailIndexByInput(inputElement: HTMLElement): number {
    // Find the closest email field container
    const emailContainer = inputElement.closest('.email-field-container')

    if (emailContainer) {
      // Angular uses ng-reflect-name for formGroupName directive
      const formGroupName = emailContainer.getAttribute('ng-reflect-name')

      if (formGroupName) {
        const index = parseInt(formGroupName)
        return index
      }
    }
    return -1
  }

  /**
   * Get email label based on index (only for alternative emails)
   */
  getEmailLabel(index: number): string {
    return `Alternative Email ${index + 1}`
  }

  /**
   * Custom validator to ensure passwords match
   */
  private _confirmedValidator(controlName: string, matchingControlName: string) {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName]
      const matchingControl = formGroup.controls[matchingControlName]
      if (matchingControl.errors && !matchingControl.errors.passwordsNotMatching) {
        return
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ passwordsNotMatching: true })
      } else {
        matchingControl.setErrors(null)
      }
    }
  }
  /**
   * Change value to upperCase
   * @param value
   */
  upperCase(value: string[]) {
    for (const val of value) {
      const valueName = val
      this.profileForm.controls[val].valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((val: string) => {
        const word = val
          .split(' ')
          .map((w) => w.charAt(0).toUpperCase() + w.substr(1).toLowerCase())
          .join(' ')
        if (word !== val) {
          this.profileForm.controls[valueName].patchValue(word)
        }
      })
    }
  }

  /**
   * v2 upload callbacks for profile picture (keeps UI consistent with org settings)
   */
  public onProfilePictureChanged(file: { path?: string; id?: number; name?: string } | null): void {
    if (!file?.path) return
    this.profilePictureFile.patchValue({
      path: file.path,
      id: file.id ?? null,
      name: file.name ?? null
    })
    this.profileForm.controls['profile_picture'].setValue(file.path)
    this.profileForm.controls['profile_picture'].markAsDirty()
    this.hasAvatar = true
  }

  public onProfilePictureDeleted(): void {
    this.profilePictureFile.patchValue({
      path: '',
      id: null,
      name: null
    })
    this.profileForm.controls['profile_picture'].setValue('')
    this.profileForm.controls['profile_picture'].markAsDirty()
    this.hasAvatar = false
  }

  /**
   * Saves user profile information
   */
  save() {
    const formValue = Object.assign({}, this.profileForm.value)
    // Get the formatted phone number for display but send without spaces
    const phoneNumber = this.profileForm.controls['phone_number'].value
    const phoneNumberForPayload = phoneNumber.replace(/\+|\s/g, '')

    // Remove emails and primary_email from formValue as we'll handle them separately
    delete formValue['emails']
    delete formValue['primary_email']

    Object.entries(formValue).forEach((o: [string, unknown]) => {
      if (this.profileForm.controls[o[0]].pristine) {
        delete formValue[o[0]]
      } else {
        if (o[0] === 'phone_number') {
          // Send the phone number without spaces in the payload
          formValue[o[0]] = phoneNumberForPayload
        } else {
          formValue[o[0]] = !!String(o[1]).trim() ? String(o[1]).trim() : null
        }
      }
    })
    this.auth
      .updateUser({ ...formValue })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (data) => {
          this.auth.user = data
          this.toastr.success('Updated user profile ', '')
          this.profileForm.markAsPristine()
        },
        error: (error) => this.toastr.error('Failed to update user profile ', `${error.error.message}`)
      })
  }

  /**
   * Updates the user password
   */
  updatePassword() {
    // Never keep the CTA disabled (better UX); validate on click
    this.passwordForm.markAllAsTouched()
    if (this.passwordForm.invalid) {
      this.toastr.error('Please enter a valid password and confirmation.', '')
      return
    }

    // Route A: MFA enabled -> verify via OTP/code modal
    if ((this.auth.user as any)?.mfa_enabled) {
      this.passwordVerifyMode = 'mfa'
      this._resetOtp('passwordVerify')
      this.passwordVerifyModalOpen = true
      this.passwordVerifyLoading = true
      this._openLocalModal(this.passwordVerifyModalId)
      this.profileService
        .sendMfaOtp()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.passwordVerifyLoading = false
          },
          error: () => {
            this.passwordVerifyLoading = false
            this.toastr.error('Failed to start MFA verification.', '')
          }
        })
      return
    }

    const hasPassword = (this.auth.user as any)?.has_password ?? !((this.auth.user as any)?.social_login_only ?? false)

    // Route B: No MFA + no password -> email OTP fallback
    if (!hasPassword) {
      this.passwordVerifyMode = 'email'
      this._resetOtp('passwordVerify')
      this.passwordVerifyModalOpen = true
      this.passwordVerifyLoading = true
      this._openLocalModal(this.passwordVerifyModalId)
      this.profileService
        .sendChangePasswordEmailOtp()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.passwordVerifyLoading = false
          },
          error: () => {
            this.passwordVerifyLoading = false
            this.toastr.error('Failed to send verification code.', '')
          }
        })
      return
    }

    // Route C: No MFA + has password -> confirm current password in modal
    this.currentPasswordControl.setValue('')
    this.currentPasswordControl.markAsPristine()
    this.currentPasswordControl.markAsUntouched()
    this.currentPasswordModalOpen = true
    this._openLocalModal(this.currentPasswordModalId)
  }

  private _openLocalModal(id: string): void {
    setTimeout(() => {
      if (!this._modalService.isModalOpen(id)) {
        this._modalService.toggleModal(id)
      }
    }, 0)
  }

  public closeCurrentPasswordModal(): void {
    if (this._modalService.isModalOpen(this.currentPasswordModalId)) {
      this._modalService.toggleModal(this.currentPasswordModalId)
    }
    this.currentPasswordModalOpen = false
    this.currentPasswordLoading = false
    this.currentPasswordControl.setValue('')
    this.currentPasswordControl.markAsPristine()
    this.currentPasswordControl.markAsUntouched()
  }

  public confirmCurrentPassword(): void {
    this.currentPasswordControl.markAsTouched()
    const currentPassword = String(this.currentPasswordControl.value ?? '').trim()
    if (!currentPassword) {
      this.toastr.error('Please enter your current password.', '')
      return
    }
    this.currentPasswordLoading = true
    const formValue = Object.assign({}, this.passwordForm.value)
    this._submitPasswordChange({ ...formValue, current_password: currentPassword }, () => this.closeCurrentPasswordModal())
  }

  public closePasswordVerifyModal(): void {
    if (this._modalService.isModalOpen(this.passwordVerifyModalId)) {
      this._modalService.toggleModal(this.passwordVerifyModalId)
    }
    this.passwordVerifyModalOpen = false
    this.passwordVerifyLoading = false
    this.passwordVerifyMode = 'mfa'
    this._resetOtp('passwordVerify')
  }

  public confirmPasswordVerifyCode(): void {
    const code = this.passwordVerifyOtpCode.trim()
    if (!code || !/^\d{6}$/.test(code)) {
      this.toastr.error('Enter the 6-digit verification code.', '')
      return
    }
    this.passwordVerifyLoading = true
    const formValue = Object.assign({}, this.passwordForm.value)
    const proof = this.passwordVerifyMode === 'email' ? { email_code: code } : { mfa_code: code }
    this._submitPasswordChange({ ...formValue, ...proof }, () => this.closePasswordVerifyModal())
  }

  private _submitPasswordChange(
    payload: { current_password?: string; mfa_code?: string; email_code?: string; new_password: string; new_password_confirmation: string },
    onSuccess?: () => void
  ): void {
    this.profileService
      .changePassword(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.passwordForm.reset()
          Object.keys(this.passwordForm.controls).forEach((key) => {
            this.passwordForm.controls[key].markAsPristine()
            this.passwordForm.controls[key].markAsUntouched()
            this.passwordForm.controls[key].setErrors(null)
          })
          this.toastr.success('Updated password successfully ', '')
          this.showPasswordWarning = false
          onSuccess?.()
          this.passwordVerifyLoading = false
          this.currentPasswordLoading = false
        },
        error: (error) => {
          this.passwordVerifyLoading = false
          this.currentPasswordLoading = false
          this.toastr.error('Failed to update password', `${error.error?.message ?? ''}`)
        }
      })
  }

  /**
   * Returns the display name for an MFA factor (for modal headers and messages).
   */
  public getMfaFactorLabel(factor: MFA_FACTOR_TYPE): string {
    const labels: Record<MFA_FACTOR_TYPE, string> = {
      [MFA_FACTOR_TYPE.APP]: 'Authenticator App',
      [MFA_FACTOR_TYPE.EMAIL]: 'Email',
      [MFA_FACTOR_TYPE.PHONE]: 'SMS'
    }
    return labels[factor]
  }

  /**
   * Header text for the MFA modal (used by the shared modal component). Empty when modal is not in use.
   */
  get mfaModalHeader(): string {
    if (this.mfaModalFactor == null || this.mfaModalMode == null) return ''
    return this.mfaModalMode === 'enable' ? `Enable MFA using ${this.getMfaFactorLabel(this.mfaModalFactor)}` : `Disable ${this.getMfaFactorLabel(this.mfaModalFactor)}`
  }

  /**
   * Returns the current enabled state for the given MFA factor.
   */
  private _isMfaFactorEnabled(factor: MFA_FACTOR_TYPE): boolean {
    return this.enabledMFAType === factor
  }

  /**
   * Returns true when this factor's toggle should be disabled (user can only have one MFA enabled; others are unclickable).
   */
  public isMfaFactorDisabled(factor: MFA_FACTOR_TYPE): boolean {
    return this.enabledMFAType !== null && this.enabledMFAType !== factor
  }

  /**
   * Handles MFA checkbox click: opens enable modal (setup content from API) or disable confirmation modal.
   * Prevents the checkbox from toggling until the user completes the flow or cancels. No-op if this factor is disabled (another is enabled).
   */
  public onMfaToggle(factor: MFA_FACTOR_TYPE, event: Event): void {
    event.preventDefault()
    if (this.isMfaFactorDisabled(factor)) return
    const currentlyEnabled = this._isMfaFactorEnabled(factor)
    this.mfaModalFactor = factor
    this.mfaShowRecoveryCodes = false
    this.authenticatorResponse = null
    this._resetOtp('mfa')
    if (currentlyEnabled) {
      this.mfaModalMode = 'disable'
      this._openMfaModal()
      // For EMAIL/SMS, send an OTP so user can confirm disable (or use a recovery code).
      if (factor === MFA_FACTOR_TYPE.EMAIL || factor === MFA_FACTOR_TYPE.PHONE) {
        this.mfaModalLoading = true
        this._startMfaResendCooldown()
        this.profileService
          .sendMfaOtp()
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: () => {
              this.mfaModalLoading = false
            },
            error: () => {
              this.mfaModalLoading = false
              this.toastr.error('Could not send verification code.', '')
            }
          })
      }
    } else {
      this.mfaModalMode = 'enable'
      this._openMfaModal()
      if (factor === MFA_FACTOR_TYPE.APP) {
        this.mfaModalLoading = true
        this.profileService
          .enableMFA(factor)
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: (res) => {
              const data = (res as Response<Authenticator>)?.data ?? res
              this.authenticatorResponse = data
              this.mfaModalLoading = false
            },
            error: (error) => {
              this.mfaModalLoading = false
              this.toastr.error('Failed to load setup', `${error?.error?.message ?? ''}`)
            }
          })
      } else {
        this.mfaModalLoading = false
        this._startMfaResendCooldown()
        this.profileService
          .enableMFA(factor)
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: () => {},
            error: (error) => {
              this.toastr.error(`${error?.error?.message ?? 'Could not send code.'}`, '')
            }
          })
      }
    }
  }

  /**
   * Opens the MFA modal (must be called after setting mfaModalFactor and mfaModalMode).
   * Renders the modal via mfaModalOpen then syncs with ModalService after the view updates (same pattern as org-modal).
   */
  private _openMfaModal(): void {
    this.mfaModalOpen = true
    setTimeout(() => {
      if (!this._modalService.isModalOpen(this.mfaModalId)) {
        this._modalService.toggleModal(this.mfaModalId)
      }
    }, 0)
  }

  /**
   * Closes the MFA modal and resets modal state. Syncs with ModalService when closing programmatically.
   */
  public closeMfaModal(): void {
    if (this._modalService.isModalOpen(this.mfaModalId)) {
      this._modalService.toggleModal(this.mfaModalId)
    }
    this._resetMfaModalState()
  }

  /**
   * Handles modal close event from the modal component (X or backdrop). Modal already toggled the service; just clear state.
   */
  public onMfaModalClosed(): void {
    this._resetMfaModalState()
  }

  /**
   * Clears all MFA modal state (factor, mode, content, recovery codes, etc.).
   */
  private _resetMfaModalState(): void {
    this.mfaModalOpen = false
    this.mfaModalFactor = null
    this.mfaModalMode = null
    this.mfaModalLoading = false
    this.authenticatorResponse = null
    this.mfaRecoveryCodeControl.setValue('', { emitEvent: false })
    this._resetOtp('mfa')
    this.mfaShowRecoveryCodes = false
    this.mfaResendSecondsRemaining = 0
    if (this._mfaResendTimer) {
      clearInterval(this._mfaResendTimer)
      this._mfaResendTimer = null
    }
  }

  /**
   * Disables the MFA factor (no challenge for now). Calls the API and shows disabled on success.
   */
  public confirmDisableMFA(): void {
    if (this.mfaModalFactor == null) return
    // Require either OTP (6 digits) or a recovery code
    const otp = this.mfaOtpCode.trim()
    const recovery = String(this.mfaRecoveryCodeControl.value ?? '').trim()
    if (!otp && !recovery) {
      this.toastr.error('Enter a verification code or a recovery code.', '')
      return
    }

    this.mfaModalLoading = true
    this.profileService
      .disableMFA({ code: otp || undefined, recovery_code: recovery || undefined })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          const factorLabel = this.getMfaFactorLabel(this.mfaModalFactor!)
          this._setMfaEnabled(this.mfaModalFactor!, false)
          if (this.auth.user) {
            ;(this.auth.user as any).mfa_enabled = false
            ;(this.auth.user as any).mfa_channel = null
          }
          this.closeMfaModal()
          this.toastr.success(`${factorLabel} two-factor authentication disabled successfully.`, '')
        },
        error: (error) => {
          this.mfaModalLoading = false
          this.toastr.error(`${this.getMfaFactorLabel(this.mfaModalFactor!)} two-factor authentication confirmation failed. ${error.error.message ?? ''}`, '')
        }
      })
  }

  /**
   * Confirms the code from the authenticator app or email/phone OTP.
   */
  public confirmMFA(): void {
    const otp = this.mfaOtpCode.trim()
    if (!otp) {
      this.toastr.error('Please enter the code to confirm two-factor authentication', '')
      return
    }
    this.mfaModalLoading = true
    this.profileService
      .confirmMFA(otp)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          const data = (res as any)?.data ?? res
          if (data?.confirmed) {
            this._setMfaEnabled(this.mfaModalFactor!, true)
            if (this.auth.user) {
              ;(this.auth.user as any).mfa_enabled = true
              ;(this.auth.user as any).mfa_channel = this.mfaModalFactor === MFA_FACTOR_TYPE.APP ? 'APP' : this.mfaModalFactor === MFA_FACTOR_TYPE.EMAIL ? 'EMAIL' : 'SMS'
            }
            // Always fetch and display recovery codes after enabling (APP/EMAIL/SMS).
            this.profileService
              .getMfaRecoveryCodes()
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe({
                next: (codesRes) => {
                  const codesData = (codesRes as any)?.data ?? (codesRes as any)
                  const codes = codesData?.recovery_codes ?? codesData?.data?.recovery_codes ?? []
                  this.authenticatorResponse = { ...(this.authenticatorResponse ?? {}), recovery_codes: codes }
                  this.mfaShowRecoveryCodes = true
                  this.mfaModalLoading = false
                },
                error: () => {
                  this.mfaShowRecoveryCodes = true
                  this.mfaModalLoading = false
                }
              })
            this.toastr.success(`${this.getMfaFactorLabel(this.mfaModalFactor!)} two-factor authentication confirmed successfully.`, '')
          } else {
            this.mfaModalLoading = false
            this.toastr.error(`${this.getMfaFactorLabel(this.mfaModalFactor!)} two-factor authentication confirmation failed. ${data?.message ?? ''}`, '')
          }
        },
        error: (error) => {
          this.mfaModalLoading = false
          this.toastr.error(`${this.getMfaFactorLabel(this.mfaModalFactor!)} two-factor authentication confirmation failed. ${error.error.message ?? ''}`, '')
        }
      })
  }

  /** OTP (6-box) getters */
  public get mfaOtpCode(): string {
    return this._readOtp('mfa')
  }
  public get passwordVerifyOtpCode(): string {
    return this._readOtp('passwordVerify')
  }

  private _otpControls(kind: 'mfa' | 'passwordVerify'): FormControl[] {
    return kind === 'mfa' ? this.mfaOtpControls : this.passwordVerifyOtpControls
  }

  private _readOtp(kind: 'mfa' | 'passwordVerify'): string {
    return this._otpControls(kind)
      .map((c) =>
        String(c.value ?? '')
          .replace(/\D/g, '')
          .slice(-1)
      )
      .join('')
  }

  private _resetOtp(kind: 'mfa' | 'passwordVerify'): void {
    const ctrls = this._otpControls(kind)
    for (const c of ctrls) c.setValue('', { emitEvent: false })
  }

  private _focusOtp(kind: 'mfa' | 'passwordVerify', index: number): void {
    const el = document.getElementById(`${kind}-otp-${index}`) as HTMLInputElement | null
    el?.focus()
    el?.select?.()
  }

  public onOtpFocus(event: FocusEvent): void {
    const el = event.target as HTMLInputElement | null
    el?.select?.()
  }

  /** Standard 6-box OTP input behavior (numeric-only, paste/autofill distribution). */
  public onOtpInput(kind: 'mfa' | 'passwordVerify', index: number, event: Event): void {
    const input = event.target as HTMLInputElement
    const raw = String(input.value ?? '')
    const digitsOnly = raw.replace(/\D/g, '')
    const ctrls = this._otpControls(kind)

    if (!digitsOnly) {
      ctrls[index].setValue('', { emitEvent: false })
      return
    }

    // Mobile autofill/paste into a single box: distribute across the remaining boxes.
    if (digitsOnly.length > 1) {
      for (let j = 0; j < digitsOnly.length; j++) {
        const idx = index + j
        if (idx >= 6) break
        ctrls[idx].setValue(digitsOnly[j] ?? '', { emitEvent: false })
      }
      // Normalize the current physical input value to a single digit.
      input.value = digitsOnly[0] ?? ''
      const nextIndex = Math.min(index + digitsOnly.length, 5)
      setTimeout(() => this._focusOtp(kind, nextIndex), 0)
      return
    }

    const digit = digitsOnly.slice(-1)
    ctrls[index].setValue(digit, { emitEvent: false })
    input.value = digit

    if (index < 5) {
      setTimeout(() => this._focusOtp(kind, index + 1), 0)
    }
  }

  public onOtpKeydown(kind: 'mfa' | 'passwordVerify', index: number, event: KeyboardEvent): void {
    // Block non-digit printable chars.
    if (event.key.length === 1 && !/^\d$/.test(event.key)) {
      event.preventDefault()
      return
    }

    if (event.key !== 'Backspace') return
    const ctrls = this._otpControls(kind)
    const current = String(ctrls[index].value ?? '')
    if (current) {
      event.preventDefault()
      ctrls[index].setValue('', { emitEvent: false })
      return
    }
    if (index > 0) {
      event.preventDefault()
      ctrls[index - 1].setValue('', { emitEvent: false })
      setTimeout(() => this._focusOtp(kind, index - 1), 0)
    }
  }

  public onOtpPaste(kind: 'mfa' | 'passwordVerify', event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? ''
    const digitsOnly = text.replace(/\D/g, '').slice(0, 6)
    if (!digitsOnly) return
    event.preventDefault()

    const ctrls = this._otpControls(kind)
    for (let i = 0; i < 6; i++) {
      ctrls[i].setValue(digitsOnly[i] ?? '', { emitEvent: false })
    }
    const lastFilled = Math.min(digitsOnly.length - 1, 5)
    setTimeout(() => this._focusOtp(kind, lastFilled), 0)
  }

  /** Render OTP boxes with correct value for *ngFor */
  public readonly otpBoxes = Array.from({ length: 6 }, () => 0)

  /**
   * Starts or resets the 1-minute resend cooldown timer for email/SMS codes.
   */
  private _startMfaResendCooldown(): void {
    this.mfaResendSecondsRemaining = 60
    if (this._mfaResendTimer) {
      clearInterval(this._mfaResendTimer)
    }
    this._mfaResendTimer = setInterval(() => {
      if (this.mfaResendSecondsRemaining > 0) {
        this.mfaResendSecondsRemaining--
      }
      if (this.mfaResendSecondsRemaining <= 0) {
        this.mfaResendSecondsRemaining = 0
        clearInterval(this._mfaResendTimer)
        this._mfaResendTimer = null
      }
    }, 1000)
  }

  /**
   * Resends the email/SMS verification code with a 1-minute cooldown.
   */
  public resendMfaCode(): void {
    const factor = this.mfaModalFactor
    if (factor !== MFA_FACTOR_TYPE.EMAIL && factor !== MFA_FACTOR_TYPE.PHONE) return
    if (this.mfaResendSecondsRemaining > 0) return

    const resend$ = this.mfaModalMode === 'disable' ? this.profileService.sendMfaOtp().pipe(map(() => null)) : this.profileService.enableMFA(factor).pipe(map(() => null))
    resend$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: () => {
        this.toastr.success(`Verification code resent via ${factor === MFA_FACTOR_TYPE.EMAIL ? 'email' : 'SMS'}.`, '')
        this._startMfaResendCooldown()
      },
      error: () => {
        this.toastr.error(`Failed to resend ${factor === MFA_FACTOR_TYPE.EMAIL ? 'email' : 'SMS'} code.`, '')
      }
    })
  }

  /**
   * Copies all recovery codes to clipboard and shows a toast.
   */
  public copyRecoveryCodes(): void {
    const codes = this.authenticatorResponse?.recovery_codes ?? []
    const text = codes.join('\n')
    if (!text) return
    navigator.clipboard.writeText(text).then(
      () => this.toastr.success('Recovery codes copied to clipboard', ''),
      () => this.toastr.error('Failed to copy', '')
    )
  }

  /**
   * Updates the enabled flag for the given MFA factor. Only one factor can be enabled at a time; enabling one disables the others.
   */
  private _setMfaEnabled(factor: MFA_FACTOR_TYPE, enabled: boolean): void {
    this.enabledMFAType = enabled ? factor : null
  }

  onKeyDown(event) {
    const isKeyboardTypes = event.ctrlKey || event.altKey || event.key.length > 1
    const startWithCountryCode = this.profileForm['controls']['phone_number'].value.length === 0 && event.key === '+'
    const onlyNumbers = RegExp('[0-9-]').test(event.key)
    const reachedMaximum = this.profileForm['controls']['phone_number'].value.length <= 15
    if (!isKeyboardTypes && (!(startWithCountryCode || onlyNumbers) || !reachedMaximum)) {
      event.preventDefault()
    }
    const charCode = event.which ? event.which : event.keyCode
    if (event.target.value === '+' && charCode === 8) {
      event.preventDefault()
    }
    if (event.target.value && event.target.value.includes('+') && charCode === 43) {
      event.preventDefault()
    }
  }
}
